import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { usePnlCategories } from './usePnlCategories';
import type { PnlCategory } from './usePnlCategories';
import type { PnlSettings, MonthlyPnlResult } from '../services/pnlCalculationService';
import {
  calculateMonthlyPnl,
  calculateTotalsPnl,
  getMonthRange,
} from '../services/pnlCalculationService';
import { deriveConstructionIncomeExGst } from '../lib/claimFinancials';
import { logError } from "../lib/logError";
import { openRetention } from './dashboardPnlRetention';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ProjectPnlSummary {
  projectId: string;
  projectName: string;
  constructionIncome: number;
  totalDeductions: number;
  netProfit: number;
  profitPercent: number;
  wagesPercent: number;
  materialsPercent: number;
  materialsCost: number;
  wagesCost: number;
  otherCosts: number;
  patchingCost: number;
  /** Open retention: held on the project's claims minus already received (dashboardPnlRetention.ts). */
  retention: number;
  estimateProfit: number;
}

export interface MonthlyPnlTrend {
  label: string;     // "Jan 2025"
  month: number;
  year: number;
  income: number;
  costs: number;
  profit: number;
  profitPercent: number;
}

export interface CostCategory {
  name: string;
  value: number;
  color: string;
}

export interface ProjectMonthlyResults {
  projectId: string;
  projectName: string;
  monthlyResults: { key: string; result: MonthlyPnlResult }[];
}

export interface DashboardPnlData {
  perProjectPnl: ProjectPnlSummary[];
  projectMonthlyData: ProjectMonthlyResults[];
  totalIncome: number;
  totalCosts: number;
  totalProfit: number;
  totalProfitPercent: number;
  totalRetention: number;
  monthlyTrend: MonthlyPnlTrend[];
  costBreakdown: CostCategory[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const COST_COLORS = {
  Materials: '#f59e0b',  // amber
  Wages: '#3b82f6',      // blue
  Other: '#8b5cf6',      // purple
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Fetch P&L data for a single project
// ─────────────────────────────────────────────────────────────────────────────

interface ProjectRawData {
  projectId: string;
  projectName: string;
  reports: { claim_month: number; claim_year: number; subtotal: number; total_exc_gst: number; retention_amount: number; retention_received_amount: number }[];
  expenses: { category: string; month_date: string; amount: number }[];
  settings: PnlSettings;
}

async function fetchProjectPnlData(
  projectId: string,
  projectName: string,
): Promise<ProjectRawData> {
  // Parallel fetch: reports, expenses, settings
  const [reportsResult, expensesResult, settingsResult] = await Promise.all([
    supabase
      .from('claims_reports')
      .select('claim_month, claim_year, subtotal, total_exc_gst, retention_amount, retention_received_amount')
      .eq('project_id', projectId),
    // SEQ-180 fix: the project P&L tab reads manual rows only
    // (src/hooks/usePnlMonthlyData.ts:119-120), so the company dashboard has
    // to filter the same way or it double counts a month that has both a
    // manual and a Xero row for the same category. Smallest diff: one added
    // filter, no shape change.
    supabase
      .from('claims_pnl_monthly_values')
      .select('category, month_date, amount')
      .eq('project_id', projectId)
      .eq('source', 'manual'),
    supabase
      .from('project_financial_settings' as 'claims_projects')
      .select('target_material_percent, wages_multiplier, estimate_profit')
      .eq('project_id', projectId)
      .maybeSingle(),
  ]);

  // Parse settings with defaults
  const rawSettings = settingsResult.data as Record<string, unknown> | null;
  let materialPct = 30;
  if (rawSettings && 'target_material_percent' in rawSettings) {
    const raw = Number(rawSettings.target_material_percent);
    materialPct = raw > 0 ? (raw <= 1 ? raw * 100 : raw) : 30;
  }

  return {
    projectId,
    projectName,
    reports: (reportsResult.data || []).map((r) => ({
      claim_month: r.claim_month,
      claim_year: r.claim_year,
      subtotal: Number(r.subtotal) || 0,
      total_exc_gst: Number(r.total_exc_gst) || 0,
      retention_amount: Number(r.retention_amount) || 0,
      retention_received_amount: Number(r.retention_received_amount) || 0,
    })),
    expenses: (expensesResult.data || []).map((e) => ({
      category: e.category,
      month_date: e.month_date,
      amount: Number(e.amount) || 0,
    })),
    settings: {
      materialPercent: materialPct,
      wagesMultiplier: Number(rawSettings && 'wages_multiplier' in rawSettings ? rawSettings.wages_multiplier : 0) || 1.04,
      estimateProfit: Number(rawSettings && 'estimate_profit' in rawSettings ? rawSettings.estimate_profit : 0) || 0,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Calculate P&L for a project using raw data
// ─────────────────────────────────────────────────────────────────────────────

function calculateProjectPnl(
  raw: ProjectRawData,
  categories: PnlCategory[],
): { summary: ProjectPnlSummary; monthlyResults: { key: string; result: MonthlyPnlResult }[] } {
  // Build income map by month
  const incomeMap = new Map<string, { income: number; retention: number }>();
  for (const r of raw.reports) {
    const key = `${r.claim_year}-${String(r.claim_month).padStart(2, '0')}`;
    const grossIncome = deriveConstructionIncomeExGst({
      subtotal: r.subtotal,
      totalExcGst: r.total_exc_gst,
      retentionAmount: r.retention_amount,
    });
    const existing = incomeMap.get(key);
    if (existing) {
      existing.income += grossIncome;
      existing.retention += r.retention_amount;
    } else {
      incomeMap.set(key, { income: grossIncome, retention: r.retention_amount });
    }
  }

  // Build expense map by (month, category)
  const expenseMap = new Map<string, Record<string, number>>();
  for (const e of raw.expenses) {
    // month_date is "YYYY-MM-01", extract "YYYY-MM"
    const key = e.month_date.substring(0, 7);
    if (!expenseMap.has(key)) expenseMap.set(key, {});
    const m = expenseMap.get(key)!;
    m[e.category] = (m[e.category] || 0) + e.amount;
  }

  // Determine month range from all available data
  const allMonthKeys = new Set([...incomeMap.keys(), ...expenseMap.keys()]);
  if (allMonthKeys.size === 0) {
    return {
      summary: {
        projectId: raw.projectId,
        projectName: raw.projectName,
        constructionIncome: 0,
        totalDeductions: 0,
        netProfit: 0,
        profitPercent: 0,
        wagesPercent: 0,
        materialsPercent: 0,
        materialsCost: 0,
        wagesCost: 0,
        otherCosts: 0,
        patchingCost: 0,
        retention: 0,
        estimateProfit: raw.settings.estimateProfit,
      },
      monthlyResults: [],
    };
  }

  // Sort months and determine range
  const sortedKeys = [...allMonthKeys].sort();
  const firstParts = sortedKeys[0].split('-');
  const lastParts = sortedKeys[sortedKeys.length - 1].split('-');
  const monthRange = getMonthRange(
    parseInt(firstParts[1]), parseInt(firstParts[0]),
    parseInt(lastParts[1]), parseInt(lastParts[0]),
  );

  // Calculate per-month P&L
  const monthlyResults: { key: string; result: MonthlyPnlResult }[] = [];
  for (const mr of monthRange) {
    const incomeData = incomeMap.get(mr.key) || { income: 0, retention: 0 };
    const expenses = expenseMap.get(mr.key) || {};
    const result = calculateMonthlyPnl(
      incomeData.income,
      incomeData.retention,
      expenses,
      categories,
      raw.settings,
    );
    monthlyResults.push({ key: mr.key, result });
  }

  // Calculate totals
  const totals = calculateTotalsPnl(
    monthlyResults.map(m => m.result),
    categories,
    raw.settings,
  );

  // Calculate cost breakdowns
  const materialsBudget = totals.materialsBudget;
  let materialsActual = 0;
  const wagesTotal = totals.wagesWithMultiplier;
  const otherTotal = totals.otherExpensesTotal;

  // Add materials actual from expenses
  for (const [catName, amount] of Object.entries(totals.expensesByCategory)) {
    const cat = categories.find(c => c.name === catName);
    if (cat?.group === 'Materials' && cat.is_profit_deduction) {
      materialsActual += amount;
    }
  }

  // Extract patching cost from the Other group expenses
  const patchingCost = totals.expensesByCategory['Patching'] || 0;

  return {
    summary: {
      projectId: raw.projectId,
      projectName: raw.projectName,
      constructionIncome: totals.constructionIncome,
      totalDeductions: totals.totalDeductions,
      netProfit: totals.netProfit,
      profitPercent: totals.profitPercent,
      wagesPercent: totals.wagesPercent,
      materialsPercent: totals.materialsPercent,
      materialsCost: materialsBudget + materialsActual,
      wagesCost: wagesTotal,
      otherCosts: otherTotal,
      patchingCost,
      // Net open retention. totals.retention (and each month's result) stays the
      // held amount; only this summary takes off what has been received.
      retention: openRetention(raw.reports),
      estimateProfit: raw.settings.estimateProfit,
    },
    monthlyResults,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useDashboardPnl(): DashboardPnlData {
  const { categories, loading: categoriesLoading } = usePnlCategories();
  const [perProjectPnl, setPerProjectPnl] = useState<ProjectPnlSummary[]>([]);
  const [projectMonthlyData, setProjectMonthlyData] = useState<ProjectMonthlyResults[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyPnlTrend[]>([]);
  const [costBreakdown, setCostBreakdown] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (categoriesLoading) return;
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch all active projects
      const { data: projects, error: projError } = await supabase
        .from('claims_projects')
        .select('id, job_location')
        .or('status.eq.active,status.is.null');

      if (projError) throw projError;
      if (!projects || projects.length === 0) {
        setPerProjectPnl([]);
        setProjectMonthlyData([]);
        setMonthlyTrend([]);
        setCostBreakdown([]);
        setLoading(false);
        return;
      }

      // 2. Fetch all project P&L data in parallel
      const rawDataPromises = projects.map(p =>
        fetchProjectPnlData(p.id, p.job_location)
      );
      const allRawData = await Promise.all(rawDataPromises);

      // 3. Calculate P&L for each project
      const projectResults = allRawData.map(raw =>
        calculateProjectPnl(raw, categories)
      );

      // 4. Build per-project summaries
      const summaries = projectResults
        .map(r => r.summary)
        .filter(s => s.constructionIncome > 0 || s.totalDeductions > 0);
      setPerProjectPnl(summaries);

      // 4b. Expose per-project monthly data for Pareto range filtering
      const monthlyData: ProjectMonthlyResults[] = projectResults
        .filter(r => r.summary.constructionIncome > 0 || r.summary.totalDeductions > 0)
        .map(r => ({
          projectId: r.summary.projectId,
          projectName: r.summary.projectName,
          monthlyResults: r.monthlyResults,
        }));
      setProjectMonthlyData(monthlyData);

      // 5. Build monthly trend (aggregate across all projects)
      const trendMap = new Map<string, MonthlyPnlTrend>();
      for (const pr of projectResults) {
        for (const mr of pr.monthlyResults) {
          const existing = trendMap.get(mr.key);
          if (existing) {
            existing.income += mr.result.constructionIncome;
            existing.costs += mr.result.totalDeductions;
            existing.profit += mr.result.netProfit;
          } else {
            const parts = mr.key.split('-');
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            trendMap.set(mr.key, {
              label: `${MONTH_NAMES[month - 1]} ${year}`,
              month,
              year,
              income: mr.result.constructionIncome,
              costs: mr.result.totalDeductions,
              profit: mr.result.netProfit,
              profitPercent: 0, // Calculated below
            });
          }
        }
      }

      // Calculate profit % for each month
      const trend = [...trendMap.values()]
        .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
        .map(t => ({
          ...t,
          profitPercent: t.income > 0 ? (t.profit / t.income) * 100 : 0,
        }));
      setMonthlyTrend(trend);

      // 6. Build cost breakdown
      let totalMaterials = 0;
      let totalWages = 0;
      let totalOther = 0;
      for (const s of summaries) {
        totalMaterials += s.materialsCost;
        totalWages += s.wagesCost;
        totalOther += s.otherCosts;
      }
      setCostBreakdown([
        { name: 'Materials', value: totalMaterials, color: COST_COLORS.Materials },
        { name: 'Wages', value: totalWages, color: COST_COLORS.Wages },
        { name: 'Other', value: totalOther, color: COST_COLORS.Other },
      ]);

    } catch (err: unknown) {
      logError('Error fetching dashboard P&L:', err);
      setError(err instanceof Error ? err.message : 'Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  }, [categories, categoriesLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Computed aggregates
  const totalIncome = perProjectPnl.reduce((s, p) => s + p.constructionIncome, 0);
  const totalCosts = perProjectPnl.reduce((s, p) => s + p.totalDeductions, 0);
  const totalProfit = totalIncome - totalCosts;
  const totalProfitPercent = totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0;
  const totalRetention = perProjectPnl.reduce((s, p) => s + p.retention, 0);

  return {
    perProjectPnl,
    projectMonthlyData,
    totalIncome,
    totalCosts,
    totalProfit,
    totalProfitPercent,
    totalRetention,
    monthlyTrend,
    costBreakdown,
    loading: loading || categoriesLoading,
    error,
    refresh: fetchData,
  };
}
